/* eslint-disable @typescript-eslint/no-unused-vars */
import "@/styles/globals.css";

/** https://mui.com/material-ui/integrations/nextjs/ "Pages Router" */
import type { AppProps } from "next/app";
import { PropsWithChildren, useMemo } from "react";
import { AppCacheProvider } from "@mui/material-nextjs/v14-pagesRouter";
import { ThemeProvider } from "@mui/material/styles";

import ServerSidePageLogic from "@/backend/logic/ServerSidePageLogic";

import { MainLayout } from "@/src/component/layout/MainLayout";
import { ToolKit } from "@/src/component/partial/__internal__/ToolKit";
import { DisableZoomSafari } from "@/src/component/partial/DisableZoom";
import { RUM } from "@/src/component/partial/RUM";
import { Tracking } from "@/src/component/partial/Tracking";
import { KEYS } from "@/src/const";
import { AppProvider } from "@/src/hook/provider/App";
import { CMSProvider } from "@/src/hook/provider/CMS";
import { PropertyProvider } from "@/src/hook/provider/Property";
import { ToastProvider } from "@/src/hook/provider/Toast";
import LocalizationHelper from "@/src/util/helper/LocalizationHelper";
import { SharedConfigManager } from "@/src/util/ConfigManager";

import createTheme from "@/styles/theme";

/**
 * init the shared config to dynamic set the website domain for the logger
 */
function setConfig(hostname: string) {
  if (!hostname) {
    return;
  }
  SharedConfigManager.init({
    [KEYS.DOMAIN]: (hostname ?? "").replace(/\./g, "_"),
  });
  return true;
}

/**
 * allow all layouts to be smoothly transitioned
 */
function DetermineLayout({ Component, pageProps }: AppProps) {
  switch (pageProps.layout ?? "default") {
    case "main":
    case "primary":
    case "alternate":
      return (
        <MainLayout
          webConfig={pageProps.webConfig}
          page={pageProps.page}
          query={pageProps.query}
          nav={pageProps.nav}
          domainData={pageProps.domainData}
          marketingSources={pageProps.marketingSources}
          homePath={pageProps.homePath}
        >
          <Component {...pageProps} />
        </MainLayout>
      );
    default:
      return <Component {...pageProps} />;
  }
}

function WithPropertyProvider({ children, pageProps }: PropsWithChildren<AppProps>) {
  if (pageProps?.propertySlug) {
    return (
      <PropertyProvider
        floorplans={pageProps.floorplans}
        externalRefId={pageProps.externalRefId}
        externalRefType={pageProps.externalRefType}
        unitCarousel={pageProps.page?.property?.data?.attributes?.unitCarousel}
        defaultQuery={pageProps.query}
        property={pageProps.page?.property}
      >
        {children}
      </PropertyProvider>
    );
  }
  return <>{children}</>;
}

export default function App(props: AppProps) {
  const { Component, pageProps, router } = props;
  const hostname = pageProps.domain ?? pageProps.domainData?.hostname;
  useMemo(() => {
    setConfig(hostname);
  }, [hostname]);
  const locale = pageProps.locale ?? "en";
  const direction = LocalizationHelper.localeDirection(locale);
  const trackingEnabled: boolean = typeof pageProps.trackingEnabled === "boolean" ? pageProps.trackingEnabled : true;
  const tracking = pageProps?.tracking ?? pageProps.webConfig?.tracking;
  //memo used to prevent the theme from re-rendering when no changes, many components use theme
  //we use SSR + query props when can cause this component to re-run
  const theme = useMemo(() => createTheme(direction), [direction]);
  const rootPrefix = ServerSidePageLogic.getPathRootPrefix(hostname);

  return (
    <>
      <DisableZoomSafari />
      {trackingEnabled && tracking && (
        <>
          <Tracking
            tracking={tracking}
            marketingSources={pageProps.marketingSources}
          />
          <RUM hostname={hostname} />
        </>
      )}
      <CMSProvider
        locale={pageProps.locale}
        webConfig={pageProps.webConfig}
        defaultQuery={pageProps.query}
        propertySlug={pageProps.propertySlug}
        rootPrefix={rootPrefix}
        galleries={pageProps.galleries}
      >
        <AppProvider
          defaultQuery={pageProps.query ?? ""}
          defaultHeader={pageProps.header}
          defaultBackgroundColor={pageProps.backgroundColor}
          defaultDomain={pageProps.domainData?.hostname}
        >
          <AppCacheProvider {...props}>
            <ThemeProvider theme={theme}>
              <ToastProvider>
                <WithPropertyProvider {...props}>
                  <ToolKit enable={pageProps.isToolkit}>
                    <DetermineLayout
                      router={router}
                      Component={Component}
                      pageProps={pageProps}
                    />
                  </ToolKit>
                </WithPropertyProvider>
              </ToastProvider>
            </ThemeProvider>
          </AppCacheProvider>
        </AppProvider>
      </CMSProvider>
    </>
  );
}
